/**
 * WorkspaceWidgetSettingsSheet.js
 *
 * Per-widget settings: time range (overrides the workspace period), store (for
 * accounts with several stores) and how many rows a list-like widget shows.
 * Every tap is applied immediately (the store debounces the save), so there is no
 * Save button - same behaviour as the dashboard customize screen.
 *
 * @module components/workspaces/WorkspaceWidgetSettingsSheet
 */

import React from 'react';
import {View} from 'react-native';
import {useTranslation} from 'react-i18next';
import PremiumSheet, {SheetChip, SheetLabel} from '../management/PremiumSheet';
import haptics from '../../helpers/haptics';
import {resolveWidgetDefinition} from '../../config/workspaceCatalog';
import {WORKSPACE_PERIODS} from '../../helpers/workspaces/workspaceModel';
import {useWorkspaceLabels} from './WorkspaceUI';

const ROW_CHOICES = [3, 5, 8, 10];
const wrap = {flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 16};

/**
 * @param {object} props
 * @param {boolean} props.visible Whether the sheet is shown.
 * @param {() => void} props.onClose Dismiss.
 * @param {?{id: string, type: string, options?: object}} props.widget Widget being configured.
 * @param {Array<{id: string, name: string}>} props.stores Stores the user can pick (picker hidden when fewer than 2).
 * @param {(patch: object) => void} props.onChange Patch the widget options (`null` clears a key).
 * @returns {JSX.Element} The sheet.
 */
export default function WorkspaceWidgetSettingsSheet({visible, onClose, widget, stores, onChange}) {
    const {t} = useTranslation();
    const labels = useWorkspaceLabels();
    const options = widget?.options || {};
    const kind = widget ? resolveWidgetDefinition(widget.type).kind : null;
    const hasRows = kind === 'list' || kind === 'breakdown' || kind === 'actions';

    const pick = (patch) => {
        haptics.select();
        onChange(patch);
    };

    return (
        <PremiumSheet
            visible={visible}
            onClose={onClose}
            title={t('wsd_settings_title')}
            subtitle={widget ? labels.title(widget.type) : ''}
            icon="tune-variant"
            hideFooter
        >
            <SheetLabel>{t('wsd_time_range')}</SheetLabel>
            <View style={wrap}>
                <SheetChip label={t('wsd_use_default')} active={!options.period} onPress={() => pick({period: null})} />
                {WORKSPACE_PERIODS.map((period) => (
                    <SheetChip key={period} label={t(`wsd_period_${period}`)} active={options.period === period} onPress={() => pick({period})} />
                ))}
            </View>

            {stores.length > 1 && (
                <>
                    <SheetLabel>{t('wsd_store')}</SheetLabel>
                    <View style={wrap}>
                        <SheetChip label={t('wsd_use_default')} active={!options.restaurantId} onPress={() => pick({restaurantId: null})} />
                        {stores.map((store) => (
                            <SheetChip key={store.id} label={store.name} active={options.restaurantId === store.id} onPress={() => pick({restaurantId: store.id})} />
                        ))}
                    </View>
                </>
            )}

            {hasRows && (
                <>
                    <SheetLabel>{t('wsd_rows')}</SheetLabel>
                    <View style={wrap}>
                        <SheetChip label={t('wsd_use_default')} active={!options.limit} onPress={() => pick({limit: null})} />
                        {ROW_CHOICES.map((limit) => (
                            <SheetChip key={limit} label={String(limit)} active={options.limit === limit} onPress={() => pick({limit})} />
                        ))}
                    </View>
                </>
            )}
        </PremiumSheet>
    );
}
